import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MarketingSourceFunnelRow } from '@/lib/api/marketing-report-service';

interface Props {
  sourceFunnel: MarketingSourceFunnelRow[];
  /** Total sessions that reached /contact, shown as the card headline. */
  reached: number;
}

const pct = (num: number | null, den: number | null) =>
  num != null && den != null && den > 0 ? `${Math.round((1000 * num) / den) / 10}%` : null;

/**
 * The whole journey per source in one table: sessions → engaged → reached /contact →
 * leads, with the end-to-end conversion rate. Replaces three separate tables (Traffic
 * Sources, Contact Page Sources, Leads Sources) that had to be cross-referenced by eye.
 *
 * A dash means "not recorded", deliberately not zero — a source with traffic but no
 * leads is a real finding, and distinguishable from one we have no data for. The lead
 * column comes from a different system than the GA4 columns (see the server-side join
 * note), which is why a source can carry leads with no session data, e.g. "(not set)".
 */
export function MarketingSourceFunnelTable({ sourceFunnel, reached }: Props) {
  // Totals sum only the values actually recorded; nulls contribute nothing.
  const sum = (pick: (r: MarketingSourceFunnelRow) => number | null) =>
    sourceFunnel.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
  const totalSessions = sum(r => r.sessions);
  const totalEngaged = sum(r => r.engagedSessions);
  const totalContact = sum(r => r.contactSessions);
  const totalLeads = sum(r => r.leads);

  if (sourceFunnel.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Sources → Contact → Leads</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">No source data for this range.</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>
          Sources → Contact → Leads
          <span className="ml-2 text-base font-normal text-muted-foreground">
            {reached} reached /contact · {totalLeads} lead{totalLeads !== 1 ? 's' : ''}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Source</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Sessions</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Engaged</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Eng. Rate</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">→ /contact</th>
              <th className="px-2 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Leads</th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">Conv. Rate</th>
            </tr>
          </thead>
          <tbody>
            {sourceFunnel.map((r, i) => {
              const dash = <span className="text-muted-foreground">—</span>;
              return (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 max-w-[200px]">
                    <span className="flex items-baseline gap-1 min-w-0">
                      <span className="text-xs text-muted-foreground uppercase shrink-0">{r.type}</span>
                      <span className="truncate" title={r.label}>{r.label}</span>
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                    {r.sessions != null ? r.sessions.toLocaleString() : dash}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                    {r.engagedSessions != null ? r.engagedSessions.toLocaleString() : dash}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap text-muted-foreground">
                    {pct(r.engagedSessions, r.sessions) ?? dash}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                    {r.contactSessions != null ? r.contactSessions.toLocaleString() : dash}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap font-medium">
                    {r.leads != null ? r.leads.toLocaleString() : <span className="font-normal">{dash}</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {pct(r.leads, r.sessions) ?? dash}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Rates here are WEIGHTED (total ÷ total), never an average of the per-row
              percentages — averaging rates would let a 1-session source count as much
              as a 1,200-session one. */}
          <tfoot>
            <tr className="border-t-2 font-medium">
              <td className="px-3 py-2">Total</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalSessions.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalEngaged.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{pct(totalEngaged, totalSessions) ?? '—'}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalContact.toLocaleString()}</td>
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{totalLeads.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{pct(totalLeads, totalSessions) ?? '—'}</td>
            </tr>
          </tfoot>
        </table>
        {sourceFunnel.some(r => r.label === '(not set)') && (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            “(not set)” = leads created before attribution capture shipped, so their source
            is unknown. Leads captured since, with no referrer, count as Direct.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
